// E15-t3 — THE STATE CHIP: the format's own vocabulary, given a form.
//
// Contract: web/design/forge-prototype.html — `.chip`, transcribed:
//
//     .chip { font-family: var(--mono); font-size: 10.5px; font-weight: 650;
//             padding: 2px 7px; border-radius: 3px; letter-spacing: .02em;
//             display: inline-flex; align-items: center; gap: 5px; }
//     .chip::before { content: ""; width: 5px; height: 5px;
//                     border-radius: 1px; background: currentColor; }
//     .s-built { color: var(--built); background: var(--built-w); }   … ×5
//
// Contract: vault/Conventions/Forge Design Language.md — "Chips carry state.
//   Mono 10.5px, border-radius: 3px, a 5px square dot before the text,
//   foreground and a washed background per state … The dot is what survives a
//   greyscale print or a colour-blind reader."
// Contract: vault/Backlog/E15 Forge Design Language.md t3.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS IS NOT A COMPOSED `Badge` + `StatusDot`, which was the first thing
// tried and which the design system genuinely does ship.
//
// 1. BADGE DISAGREES WITH THE CHIP ON FIVE OF ITS SIX DECLARATIONS. Read out of
//    the compiled `astryx.css`, `Badge`'s base rule is:
//
//        border-radius: var(--radius-full)        chip: 3px  (--radius-inner)
//        font-family:   inherit                   chip: the MONO stack
//        font-size:     var(--text-supporting-size)  (12.5)  chip: 10.5
//        height:        var(--spacing-5)          chip: content + 2px padding
//        padding-inline:var(--spacing-2)          chip: 7px
//        font-weight:   var(--font-weight-medium) chip: semibold
//
//    A pill in the body face at the metadata size is a different object from a
//    3px mono tag at the label size. Composing it would mean overriding all six.
//
// 2. AND THOSE OVERRIDES CANNOT BE WRITTEN. Astryx styles with StyleX and emits
//    every rule as `.x3nfvp2:not(#\#):not(#\#):not(#\#)` — three ID-level
//    guards, specificity (3,1,0). A stylesheet of ours tops out at (0,2,0), so
//    a `.dj-chip` rule LOSES to Badge's own every time. The library's escape
//    hatch is the `xstyle` prop, which takes COMPILED StyleX objects; this app
//    does not compile StyleX (src/theme.ts records the same fact from the other
//    side — `<Theme>` renders with an empty className because of it) and does
//    not depend on `@stylexjs/stylex` at all. So there is no supported way to
//    make Badge into this chip. This is a measured constraint, not a taste.
//
// 3. STATUSDOT IS FIXED AT 8px AND `border-radius: 50%`, and it is a standalone
//    `role="img"` with its own accessible name. The chip's mark is 5px, square,
//    and NOT independently announced — it is a redundant channel for the word
//    beside it, and giving it a second accessible name would make every chip
//    read twice to a screen reader.
//
// WHAT IS BORROWED ANYWAY, because it is exactly what the design system is for:
// the LABEL is an Astryx `Text`, and its face, size and weight are props that
// name theme tokens rather than declarations in our stylesheet —
//
//   font-family  ← type="code"       → var(--font-family-code)
//   font-size    ← size="3xs"        → var(--font-size-3xs) = 10.5
//   font-weight  ← weight="semibold" → var(--font-weight-semibold) = 600
//   colour       ← color="inherit"   → the state hue, set on the wrapper
//
// the same mapping src/components/Eyebrow.tsx makes, and for the same reason:
// src/theme.ts is where a value lives. `color="inherit"` is the load-bearing
// one — it is the single prop that lets a state hue our CSS owns reach a label
// Astryx styles, without either side out-specifying the other.
//
// THE WEIGHT IS 600 AND THE PROTOTYPE SAYS 650. `--font-weight-semibold` is
// 600 and the theme's four weight rungs are 400/560/600/700; 650 is a value
// src/theme.ts spends on HEADINGS (`--text-display-*-weight`). Reaching for a
// heading's weight for a 10.5px tag, or minting a fifth rung to gain fifty
// units at that size, both cost more than the difference is worth. Named, so
// the divergence is a decision on the record rather than a drift.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE WORDS ARE NOT OPTIONAL. A chip takes its label from the caller and never
// derives one, because the two places this is used today already say something
// true in English — "Green — this documentation parses", "Not rendered yet" —
// and E15-t3's instruction is that the chip gives those words a form rather
// than replacing them with a colour. A component that rendered `state` as its
// own text would quietly invite exactly that replacement.
import type { ReactNode } from 'react';

import { Text } from '@astryxdesign/core/Text';

import './chip.css';

/**
 * The five states `format.yaml` declares — built, partial, missing, drift,
 * unspecified — which the vault maps onto success/warning/error/info/muted and
 * which src/theme.ts maps onto Astryx's green/yellow/red/blue/gray categorical
 * quads. This union is deliberately the FORMAT's vocabulary and not any one
 * page's: a page with three answers of its own (a referee verdict, a render
 * status) maps its words onto these five, and that mapping is written down
 * beside the words it belongs to.
 */
export type ChipState = 'built' | 'partial' | 'missing' | 'drift' | 'unspecified';

export interface ChipProps {
  /** which of the format's five states this chip is drawn in */
  state: ChipState;
  /** the words. A machine token (`green`, `drift`) or a short status phrase —
   *  never a sentence: prose is the sans face and this is the mono one. */
  children: ReactNode;
}

export function Chip({ state, children }: ChipProps) {
  return (
    // `data-state` mirrors the class so a test — and a Playwright spec at the
    // altitude where colour is actually resolvable — can address a chip by the
    // state it claims rather than by the words that happen to be in it.
    <span className={`dj-chip dj-chip-${state}`} data-state={state}>
      <Text type="code" size="3xs" weight="semibold" color="inherit">
        {children}
      </Text>
    </span>
  );
}

export default Chip;
